import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.isConnected = false;
    this.currentSessionId = null;
  }

  connect(token, sessionId = null) {
    if (this.socket?.connected) {
      console.log('🔌 Socket already connected');
      // If sessionId changed, reconnect
      if (sessionId && sessionId !== this.currentSessionId) {
        console.log('🔌 Session ID changed, reconnecting...');
        this.disconnect();
      } else {
        return;
      }
    }
    
    if (!token) {
      console.log('❌ Cannot connect socket: No JWT token provided');
      return;
    }

    console.log('🔌 Connecting to Socket.IO server...', sessionId ? `(session: ${sessionId})` : '');

    // Use environment variable or derive from current location
    const socketUrl = process.env.REACT_APP_SOCKET_URL ||
                     process.env.REACT_APP_API_URL ||
                     `${window.location.protocol}//${window.location.hostname}:3001`;

    console.log('🔌 Socket URL:', socketUrl);

    // Build query params
    const query = { token };
    if (sessionId) {
      query.sessionId = sessionId;
      this.currentSessionId = sessionId;
    }

    this.socket = io(socketUrl, {
      query: query,
      transports: ['websocket', 'polling'],
      autoConnect: true
    });

    this.socket.on('connect', () => {
      console.log('✅ Socket connected:', this.socket.id);
      this.isConnected = true;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      this.isConnected = false;
    });

    this.socket.on('authenticated', (data) => {
      console.log('✅ Socket authenticated for user:', data.username);
      if (data.sessionId) {
        this.currentSessionId = data.sessionId;
        console.log('✅ Socket joined session:', data.sessionId);
      }
    });

    this.socket.on('auth-error', (data) => {
      console.error('❌ Socket authentication failed:', data.error);
      this.disconnect();
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error);
      this.isConnected = false;
    });
  }

  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting socket...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentSessionId = null;
    }
  }

  /**
   * Join a session room (called by SessionStateManager)
   * Emits a socket event to actually join the room on the server
   */
  joinSessionRoom(sessionId) {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot join session room: Socket not connected');
      return;
    }

    // If already in this room, skip
    if (this.currentSessionId === sessionId) {
      return;
    }

    this.currentSessionId = sessionId;
    
    // Emit event to server to join the room
    console.log(`📡 Requesting to join session room: ${sessionId}`);
    this.socket.emit('join-session-room', { sessionId });
    
    // Set up success handler (one-time)
    this.socket.once('join-session-room-success', (data) => {
      console.log(`✅ Successfully joined session room: ${data.sessionId}`);
    });
    
    // Set up error handler (one-time)
    this.socket.once('join-session-room-error', (error) => {
      console.error(`❌ Failed to join session room:`, error);
    });
  }

  /**
   * Update session state on the server
   */
  updateSessionState(sessionId, state) {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot update session state: Socket not connected');
      return;
    }

    if (!sessionId) {
      console.warn('⚠️ Cannot update session state: No session ID');
      return;
    }

    console.log(`📡 Emitting update-session-state: ${sessionId} -> ${state}`);
    this.socket.emit('update-session-state', { sessionId, state });
  }

  /**
   * Listen for session state changes from server
   */
  onSessionStateChanged(callback) {
    if (!this.socket) return;
    
    this.socket.on('session-state-changed', (data) => {
      console.log('📡 Received session-state-changed event:', data);
      // Dispatch custom event for SessionStateManager
      window.dispatchEvent(new CustomEvent('server-session-state-changed', {
        detail: data
      }));
      if (callback) callback(data);
    });
    
    this.socket.on('session-config', (data) => {
      console.log('📡 Received session-config event:', data);
      window.dispatchEvent(new CustomEvent('server-session-config', {
        detail: data
      }));
    });
  }

  /**
   * Add a hero to a session (for tracking purposes)
   */
  addSessionHero(sessionId, heroId, userId, heroName, isAutoJoin = false) {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot add session hero: Socket not connected');
      return false;
    }

    if (!sessionId || !heroId || !userId || !heroName) {
      console.warn('⚠️ Cannot add session hero: Missing required fields');
      return false;
    }

    console.log(`📡 Emitting add-session-hero: ${heroName} (${heroId}) to session ${sessionId}${isAutoJoin ? ' [Auto-Join]' : ''}`);
    this.socket.emit('add-session-hero', {
      sessionId,
      heroId,
      userId,
      heroName,
      isAutoJoin
    });
    return true;
  }

  // XP Spending Methods
  spendXP(data) {
    if (!this.socket?.connected) {
      console.error('❌ Cannot spend XP: Socket not connected');
      return false;
    }

    console.log('💰 Emitting spend-xp event:', data);
    this.socket.emit('spend-xp', data);
    return true;
  }

  spendXPSecondary(data) {
    if (!this.socket?.connected) {
      console.error('❌ Cannot spend XP: Socket not connected');
      return false;
    }

    console.log('💰 Emitting spend-xp-secondary event:', data);
    this.socket.emit('spend-xp-secondary', data);
    return true;
  }

  getAttributeCosts(data) {
    if (!this.socket?.connected) {
      console.error('❌ Cannot fetch attribute costs: Socket not connected');
      return false;
    }

    console.log('📊 Emitting get-attribute-costs event:', data);
    this.socket.emit('get-attribute-costs', data);
    return true;
  }

  // Event Listeners
  onXPSpent(callback) {
    if (!this.socket) return;
    
    this.socket.on('xp-spent', (data) => {
      console.log('💰 Received xp-spent event:', data);
      callback(data);
    });
  }

  onXPError(callback) {
    if (!this.socket) return;
    
    this.socket.on('xp-error', (data) => {
      console.error('❌ Received xp-error event:', data);
      callback(data);
    });
  }

  onAuthenticated(callback) {
    if (!this.socket) return;
    
    this.socket.on('authenticated', callback);
  }

  onAuthError(callback) {
    if (!this.socket) return;
    
    this.socket.on('auth-error', callback);
  }

  onAttributeCosts(callback) {
    if (!this.socket) return;
    
    this.socket.on('attribute-costs', (data) => {
      console.log('📊 Received attribute-costs event:', data);
      callback(data);
    });
  }

  onAttributeCostsError(callback) {
    if (!this.socket) return;
    
    this.socket.on('attribute-costs-error', (data) => {
      console.error('❌ Received attribute-costs-error event:', data);
      callback(data);
    });
  }

  onAccountClaimed(callback) {
    if (!this.socket) return;
    
    this.socket.on('account-claimed', (data) => {
      console.log('🔗 Received account-claimed event:', data);
      callback(data);
    });
  }

  onHeroImageUpdated(callback) {
    if (!this.socket) return;
    
    this.socket.on('hero-image-updated', (data) => {
      console.log('🖼️ Received hero-image-updated event:', data);
      callback(data);
    });
  }

  reorderHeroInventory(data) {
    if (!this.socket?.connected) {
      return Promise.reject(new Error('Socket not connected'));
    }

    return new Promise((resolve, reject) => {
      this.socket.emit('inventory-reorder', data, (response) => {
        if (response?.success) {
          resolve(response);
        } else {
          reject(new Error(response?.error || 'Failed to reorder inventory'));
        }
      });
    });
  }

  // Real-time potion consumption tracking
  consumePotion(data) {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot send potion consumption: Socket not connected');
      return false;
    }

    // Emit without waiting for response (fire and forget for performance)
    this.socket.emit('potion-consumed', data);
    return true;
  }

  // Real-time ammo consumption tracking (arrows, etc.)
  consumeAmmo(data) {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot send ammo consumption: Socket not connected');
      return false;
    }

    // Emit without waiting for response (fire and forget for performance)
    this.socket.emit('ammo-consumed', data);
    return true;
  }

  // Request hero inventory refresh via socket
  refreshHeroInventory(heroId, sessionId = null) {
    if (!this.socket?.connected) {
      console.warn('⚠️ Cannot refresh hero inventory: Socket not connected');
      return false;
    }

    // Emit refresh request - server will respond via hero-inventory-updated event
    this.socket.emit('refresh-hero-inventory', {
      heroId,
      sessionId,
    });
    console.log(`[SocketService] Requested inventory refresh for hero ${heroId}`);
    return true;
  }

  // Utility Methods
  removeListeners(events) {
    if (!this.socket) return;
    
    events.forEach(event => {
      this.socket.off(event);
    });
  }

  isSocketConnected() {
    return this.socket?.connected || false;
  }

  getSocketId() {
    return this.socket?.id || null;
  }
}

// Export singleton instance
export const socketService = new SocketService();
